import React, { useState } from 'react';
import { Gift, Baby, Users, BookOpen, Paintbrush, Heart, Star, Shield, Clock } from 'lucide-react';

const ForKidsPage = ({ setCurrentPage }) => {
  const [selectedAge, setSelectedAge] = useState('all');

  const ageGroups = [
    { id: '0-2', name: '0-2 år', icon: Baby, color: 'pink', description: 'Spedbarn og småbarn' },
    { id: '3-6', name: '3-6 år', icon: Users, color: 'blue', description: 'Førskole' },
    { id: '7-12', name: '7-12 år', icon: BookOpen, color: 'green', description: 'Skolealder' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-warning-50 via-orange-50 to-error-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">👶</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver til barn</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Skap magiske barndomsminner med gaver som både underholder og lærer bort noe nytt. 
              Fra babys første leker til kreative utfoldelser for større barn.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-purple-600 text-white rounded-lg hover:from-primary-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn barnegaver
            </button>
          </div>
        </div>
      </section>

      {/* Developmental Approach */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gaver som støtter barnets utvikling
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100 text-center">
                <BookOpen className="w-12 h-12 text-accent-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">Kognitiv utvikling</h3>
                <p className="text-sm text-secondary-600">
                  Puslespill, byggeblokker og lærespill som utfordrer tenkning og problemløsning
                </p>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100 text-center">
                <Users className="w-12 h-12 text-success-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">Sosial utvikling</h3>
                <p className="text-sm text-secondary-600">
                  Rollespill og samarbeidsleker som lærer empati og kommunikasjon
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100 text-center">
                <Paintbrush className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">Kreativitet</h3>
                <p className="text-sm text-secondary-600">
                  Kunst- og håndverksmaterialer som lar fantasien løpe fritt
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-warning-50 rounded-lg p-6 border border-orange-100 text-center">
                <Heart className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-secondary-800 mb-3">Emosjonell trygghet</h3>
                <p className="text-sm text-secondary-600">
                  Kosedyr og trøsteleker som gir trygghet og tilknytning
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Age-Based Recommendations */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gaver tilpasset alder og utviklingsnivå
            </h2>

            {/* Age Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedAge('all')}
                className={`px-6 py-3 rounded-lg border transition-colors ${
                  selectedAge === 'all'
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-orange-300'
                }`}
              >
                Alle aldre
              </button>
              {ageGroups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => setSelectedAge(group.id)}
                  className={`px-6 py-3 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedAge === group.id
                      ? `bg-${group.color}-500 text-white border-${group.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <group.icon className="w-5 h-5" />
                  <span>{group.name}</span>
                </button>
              ))}
            </div>

            {/* Age Group Details */}
            <div className="space-y-12">
              {/* 0-2 years */}
              <div className={`${selectedAge === 'all' || selectedAge === '0-2' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Baby className="w-10 h-10 text-primary-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">0-2 år: Spedbarn og småbarn</h3>
                      <p className="text-secondary-600">Sensorisk utforskning og grunnleggende utvikling</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-primary-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🧸 Trygghet og tilknytning</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Myke kosedyr og tøy-leker</li>
                        <li>• Musikkleker som beroliget</li>
                        <li>• Teppe med forskjellige teksturer</li>
                        <li>• Babylater og mobile</li>
                      </ul>
                    </div>
                    
                    <div className="border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🎯 Sensorisk stimulering</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Fargerike leker med lyd</li>
                        <li>• Bitering i forskjellige former</li>
                        <li>• Aktivitetsteppe</li>
                        <li>• Enkle musikkinstrumenter</li>
                      </ul>
                    </div>
                    
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🚼 Motorisk utvikling</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Stableklosser (store og myke)</li>
                        <li>• Rullende leker å følge etter</li>
                        <li>• Gåstol eller gyngestol</li>
                        <li>• Enkle formsorterere</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <h4 className="font-semibold text-primary-800 mb-2">🛡️ Sikkerhetstips for denne alderen:</h4>
                    <p className="text-primary-700 text-sm">
                      Sørg for at alle leker er store nok til ikke å bli svelget, uten løse deler, 
                      og laget av ikke-giftige materialer. Sjekk CE-merking og aldersanbefaling.
                    </p>
                  </div>
                </div>
              </div>

              {/* 3-6 years */}
              <div className={`${selectedAge === 'all' || selectedAge === '3-6' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Users className="w-10 h-10 text-accent-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">3-6 år: Førskole og utforskning</h3>
                      <p className="text-secondary-600">Kreativitet, læring og sosial utvikling</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🎨 Kreativitet og kunst</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Tegnebøker og fargeblyanter</li>
                        <li>• Leire og formemasse</li>
                        <li>• Perlesett og enkle håndverk</li>
                        <li>• Kostumeskrin for rollespill</li>
                      </ul>
                    </div>
                    
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">📚 Læring og språk</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Bildebøker og eventybøker</li>
                        <li>• Bokstav- og tallleker</li>
                        <li>• Enkle puslespill (20-60 biter)</li>
                        <li>• Lær-nettbrett for barn</li>
                      </ul>
                    </div>
                    
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🏃 Fysisk aktivitet</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Sykkel med støttehjul</li>
                        <li>• Ball og utendørs leker</li>
                        <li>• Hinderløype-utstyr</li>
                        <li>• Sparkesykkel</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-accent-50 border border-accent-200 rounded-lg p-4">
                    <h4 className="font-semibold text-accent-800 mb-2">💡 Tips for denne alderen:</h4>
                    <p className="text-accent-700 text-sm">
                      Barn i denne alderen elsker å imitere voksne og utforske roller. 
                      Gaver som lar dem "leke voksen" som lekekjøkken, verktøykasse eller doktorsett er populære.
                    </p>
                  </div>
                </div>
              </div>

              {/* 7-12 years */}
              <div className={`${selectedAge === 'all' || selectedAge === '7-12' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <BookOpen className="w-10 h-10 text-success-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">7-12 år: Skolealder og spesialisering</h3>
                      <p className="text-secondary-600">Komplekse interesser og vennskapsrelasjoner</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🧠 Utfordringer og læring</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Komplekse LEGO-sett</li>
                        <li>• Vitenskap-eksperimenter</li>
                        <li>• Programmering for barn</li>
                        <li>• Avanserte puslespill (200+ biter)</li>
                      </ul>
                    </div>
                    
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🎮 Teknologi og gaming</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Alderpassende videospill</li>
                        <li>• Roboter de kan programmere</li>
                        <li>• Digitale tegnebrett</li>
                        <li>• Actionkameraer for barn</li>
                      </ul>
                    </div>
                    
                    <div className="border border-orange-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🏆 Hobbyer og interesser</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Sportsutstyr for deres aktivitet</li>
                        <li>• Musikkinstrumenter</li>
                        <li>• Samle-kort og samlerobjekter</li>
                        <li>• Hobby-startersett (astronomi, geologi)</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-success-50 border border-success-200 rounded-lg p-4">
                    <h4 className="font-semibold text-success-800 mb-2">🌟 Tips for denne alderen:</h4>
                    <p className="text-success-700 text-sm">
                      Barn i skolealder begynner å utvikle spesifikke interesser og kan være mer kritiske til gaver. 
                      Spør gjerne hva de er interessert i for øyeblikket eller hva vennene deres har.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety and Quality */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Sikkerhet og kvalitet kommer først
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-error-50 border border-error-200 rounded-lg p-6">
                <Shield className="w-12 h-12 text-error-500 mb-4" />
                <h3 className="text-xl font-semibold text-error-800 mb-4">Sikkerhetskrav</h3>
                <ul className="space-y-2 text-error-700">
                  <li>• Alltid sjekk aldersanbefaling på emballasjen</li>
                  <li>• Se etter CE-merking som viser EU-godkjenning</li>
                  <li>• Unngå små deler til barn under 3 år</li>
                  <li>• Velg ikke-giftige materialer og farger</li>
                  <li>• Sjekk at leker ikke har skarpe kanter</li>
                </ul>
              </div>

              <div className="bg-success-50 border border-success-200 rounded-lg p-6">
                <Star className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-success-800 mb-4">Kvalitetsmerker</h3>
                <ul className="space-y-2 text-success-700">
                  <li>• Velg kjente merker med godt rykte</li>
                  <li>• Les anmeldelser fra andre foreldre</li>
                  <li>• Invester i holdbare materialer som tåler lek</li>
                  <li>• Sjekk garantivilkår og reparasjonsmuligheter</li>
                  <li>• Vurder miljøvennlige og bærekraftige alternativer</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Budget Guidelines */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Budsjettguide for barnegaver
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-warning-50 to-orange-50 rounded-lg p-6 text-center border border-warning-100">
                <div className="text-3xl font-bold text-warning-600 mb-2">50-200 kr</div>
                <h3 className="text-lg font-semibold text-secondary-800 mb-2">Små gaver</h3>
                <p className="text-sm text-secondary-600">
                  Bursdag i barnehagen, takk eller små oppmerksomheter
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 text-center border border-accent-100">
                <div className="text-3xl font-bold text-accent-600 mb-2">200-500 kr</div>
                <h3 className="text-lg font-semibold text-secondary-800 mb-2">Vanlige gaver</h3>
                <p className="text-sm text-secondary-600">
                  Bursdagsgaver fra venner eller større søskengaver
                </p>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 text-center border border-success-100">
                <div className="text-3xl font-bold text-success-600 mb-2">500-1000 kr</div>
                <h3 className="text-lg font-semibold text-secondary-800 mb-2">Spesielle anledninger</h3>
                <p className="text-sm text-secondary-600">
                  Store bursdager, jul eller anledningsgaver fra familie
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 text-center border border-purple-100">
                <div className="text-3xl font-bold text-purple-600 mb-2">1000+ kr</div>
                <h3 className="text-lg font-semibold text-secondary-800 mb-2">Store investeringer</h3>
                <p className="text-sm text-secondary-600">
                  Sykler, store LEGO-sett eller teknologi fra besteforeldre
                </p>
              </div>
            </div>

            <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
              <Clock className="w-6 h-6 text-warning-600 mb-2" />
              <h3 className="text-lg font-semibold text-secondary-800 mb-2">Husk: Opplevelser er også gaver!</h3>
              <p className="text-secondary-700">
                Ikke alle gaver trenger å være fysiske ting. Kinobilletter, opplevelsesparker, 
                klatring eller andre aktiviteter kan være like verdifulle og skaper minner som varer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-error-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å glede et barn?
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              Bruk vår gavefinner for å få alderstilpassede anbefalinger som støtter barnets utvikling
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-orange-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('recipient')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-orange-600 transition-colors font-semibold"
              >
                Se andre mottakere
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForKidsPage;